import type { MediaEnvironment } from 'types/apis/mediaInterfaces';
import type { ProjectEnvironment } from 'types/apis/pilotInterfaces';

/** App-level environments used for promotion rules and UI labels. */
export type AppEnvironment = 'dev' | 'productqa' | 'uat' | 'nonprod' | 'prod';

export type PromotableAppEnvironment = 'dev' | 'productqa' | 'uat';

/** Valid promotion targets from each source environment. */
export const PROMOTION_TARGETS: Record<PromotableAppEnvironment, AppEnvironment[]> = {
  dev: ['uat', 'productqa'],
  productqa: ['dev', 'uat'],
  uat: ['nonprod', 'prod'],
};

export const resolveAppEnvironment = (toolApiUrl?: string): AppEnvironment => {
  const url = toolApiUrl ?? process.env.REACT_APP_TOOL_API_URL ?? '';
  if (url.includes('deto-prod-api.bidgely.com')) return 'uat';
  if (url.includes('productqa')) return 'productqa';
  return 'dev';
};

export const formatAppEnvLabel = (env: AppEnvironment, options?: { lowercase?: boolean }): string => {
  const labels: Record<AppEnvironment, string> = {
    dev: 'dev',
    productqa: 'PRODUCTQA',
    uat: 'UAT',
    nonprod: 'NON-PROD',
    prod: 'PROD',
  };

  const label = labels[env];
  return options?.lowercase ? label.toLowerCase() : label;
};

export const isPromotableAppEnvironment = (env: AppEnvironment): env is PromotableAppEnvironment =>
  env === 'dev' || env === 'productqa' || env === 'uat';

export const getPromoteTargets = (current: AppEnvironment): AppEnvironment[] =>
  isPromotableAppEnvironment(current) ? PROMOTION_TARGETS[current] : [];

const normalizeEnvToken = (value: string): string =>
  value.trim().toLowerCase().replace(/[\s_-]+/g, '');

export const projectEnvironmentToAppEnv = (env: ProjectEnvironment): AppEnvironment | null => {
  const tokens = [
    normalizeEnvToken(String(env.type ?? '')),
    normalizeEnvToken(String(env.environment_name ?? env.name ?? '')),
    normalizeEnvToken(String(env.stack_name ?? '')),
  ].filter(Boolean);

  for (const token of tokens) {
    if (token === 'dev') return 'dev';
    if (token.includes('productqa')) return 'productqa';
    if (token === 'uat' || token.includes('uat')) return 'uat';
    if (token.includes('nonprod')) return 'nonprod';
    if (token === 'prod' || (token.includes('prod') && !token.includes('nonprod'))) return 'prod';
  }

  return null;
};

export const getProjectEnvironmentDisplayLabel = (env: ProjectEnvironment): string => {
  const name = String(env.environment_name ?? env.name ?? '').trim();
  const stack = env.stack_name ? ` (${env.stack_name})` : '';
  const type = env.type ? ` — ${env.type}` : '';
  const label = `${name}${stack}${type}`.trim();
  return label || `Environment ${env.id}`;
};

/** Associated project envs shown in the promotion dropdown (always excludes current env). */
export const getPromotionDropdownEnvironments = (
  associatedEnvironments: ProjectEnvironment[],
  options: { currentAppEnv: AppEnvironment; currentEnvironmentId?: string | number | null },
): ProjectEnvironment[] => {
  const { currentAppEnv, currentEnvironmentId } = options;

  return associatedEnvironments.filter((env) => {
    if (currentEnvironmentId != null && String(env.id) === String(currentEnvironmentId)) {
      return false;
    }

    const appEnv = projectEnvironmentToAppEnv(env);
    if (appEnv === currentAppEnv) return false;

    return true;
  });
};

/** Environments shown in the drawer/compare row: current env first, then promotion targets. */
export const getComparisonEnvOrder = (current: AppEnvironment): AppEnvironment[] => {
  const targets = getPromoteTargets(current);
  if (targets.length === 0) return ['uat', 'nonprod', 'prod'];
  return [current, ...targets];
};

/** Environment string sent to GET /v1/media/get-images. */
export const toMediaApiEnvironmentParam = (env: AppEnvironment): string => env;

/** Map app env to media API environment param for upload. */
export const toMediaApiEnvironment = (env: AppEnvironment): MediaEnvironment => {
  if (env === 'uat') return 'uat';
  if (env === 'prod') return 'prod';
  return 'nonprod';
};

/** All app envs that may have distinct image URLs in the drawer. */
export const getAllComparisonEnvs = (current: AppEnvironment): AppEnvironment[] => {
  const ordered = getComparisonEnvOrder(current);
  const extras: AppEnvironment[] = ['uat', 'nonprod', 'prod'];
  return [...new Set([...ordered, ...extras])];
};

export const getProjectEnvironmentApiHost = (env: ProjectEnvironment): string | undefined => {
  const raw = String(env.data_server_url ?? env.api_host ?? '').trim();
  if (!raw) return undefined;

  try {
    if (raw.includes('://')) {
      return new URL(raw).host;
    }
  } catch {
    // fall through to raw host string
  }

  return raw.replace(/^\/+/, '').replace(/\/+$/, '');
};

export const mapAssociatedEnvironmentsByAppEnv = (
  environments: ProjectEnvironment[],
): Partial<Record<AppEnvironment, ProjectEnvironment>> => {
  const map: Partial<Record<AppEnvironment, ProjectEnvironment>> = {};

  for (const env of environments) {
    const appEnv = projectEnvironmentToAppEnv(env);
    if (appEnv && !map[appEnv]) {
      map[appEnv] = env;
    }
  }

  return map;
};

/** Whether a drawer column can load media (current env always; others need associated API host). */
export const isDrawerEnvironmentAvailable = (
  appEnv: AppEnvironment,
  currentAppEnv: AppEnvironment,
  associatedEnvironments: ProjectEnvironment[],
): boolean => {
  if (appEnv === currentAppEnv) return true;

  const byAppEnv = mapAssociatedEnvironmentsByAppEnv(associatedEnvironments);
  const projectEnv = byAppEnv[appEnv];
  return Boolean(projectEnv && getProjectEnvironmentApiHost(projectEnv));
};

/** Non-current drawer envs that lack an associated project environment with an API host. */
export const getDrawerEnvironmentsMissingApiHost = (
  drawerEnvs: AppEnvironment[],
  currentAppEnv: AppEnvironment,
  associatedEnvironments: ProjectEnvironment[],
): AppEnvironment[] =>
  drawerEnvs.filter(
    (appEnv) => !isDrawerEnvironmentAvailable(appEnv, currentAppEnv, associatedEnvironments),
  );
